import type { EntrScreen, EntrSession } from '@/domain/types';
import type { EntrDataTransport } from '@/transport/types';

interface SessionPortraitRefreshOptions {
  getSessionByGroup: EntrDataTransport['getSessionByGroup'];
  getSessionByTicketCode: EntrDataTransport['getSessionByTicketCode'];
  getSession: () => EntrSession | null;
  getScreen: () => EntrScreen;
  isEnabled: () => boolean;
  onSession: (session: EntrSession) => void;
  captureException: (error: unknown, tag: string) => void;
}

const REFRESH_MS = 5000;

export function useSessionPortraitRefresh(
  options: SessionPortraitRefreshOptions
) {
  let timer: number | null = null;
  let epoch = 0;
  let inFlightEpoch: number | null = null;

  function shouldRefresh(): boolean {
    return (
      options.isEnabled() &&
      options.getScreen() === 'select-photos' &&
      !document.hidden &&
      options.getSession()?.hasPendingPortraits === true
    );
  }

  function startPolling(): void {
    if (!shouldRefresh() || timer !== null || inFlightEpoch === epoch) return;
    timer = window.setTimeout(() => {
      timer = null;
      void refresh();
    }, REFRESH_MS);
  }

  function stopPolling(): void {
    epoch += 1;
    if (timer !== null) window.clearTimeout(timer);
    timer = null;
  }

  async function refresh(): Promise<void> {
    const current = options.getSession();
    if (!current || !shouldRefresh() || inFlightEpoch === epoch) return;
    const requestEpoch = epoch;
    inFlightEpoch = requestEpoch;
    try {
      const result = current.ticketCode
        ? await options.getSessionByTicketCode(current.ticketCode)
        : await options.getSessionByGroup(
            current.group.lookupId ?? current.group.id
          );
      const active = options.getSession();
      if (
        requestEpoch !== epoch ||
        !shouldRefresh() ||
        active?.id !== current.id ||
        active.group.id !== current.group.id
      ) {
        return;
      }
      if (
        result.ok &&
        result.value.id === current.id &&
        result.value.group.id === current.group.id
      ) {
        options.onSession(result.value);
      }
    } catch (error) {
      if (requestEpoch === epoch) {
        options.captureException(error, 'refresh_session_portraits');
      }
    } finally {
      if (inFlightEpoch === requestEpoch) inFlightEpoch = null;
      if (requestEpoch === epoch) startPolling();
    }
  }

  function onVisibilityChange(): void {
    stopPolling();
    if (!document.hidden) void refresh();
  }

  return { startPolling, stopPolling, onVisibilityChange };
}
